import { Box, CircularProgress, FormLabel, Radio, Stack, Typography } from '@mui/joy';
import { WarningCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { TextLine, Tooltip } from 'core/elements';
import { InputType } from 'core/enums';
import { PriceBasedOn, QuotePartUnifiedStatus } from 'quotes/enums';
import { getCostInputProps } from 'quotes/utils';
import { Color, Level, Variant } from 'theme/enums';

import { useCostFormFieldsContext } from './state';

export const ReplacementCostFormFields = () => {
  const { t } = useTranslation();

  const {
    control,
    errors,
    disablePricingFields,
    isExternalDisabled,
    isCalculating,
    isPriceLocked,
    isAlternateVersionEnabled,
    isReplacementCostCustomValue,
    priceBasedOn,
    replacementStandardCostValue,
    allowOverrideReplacementCost,
    requiredReplacementCost,
    requiredReplacementCostNumber,
    disabledIsReplacementCostCustom,
    isNoReplacementCostSource,
    formatStandardCost,
    costDecimals,
    replacementCostSourceLabel,
    onReplacementCostCustomChange,
    onBlurReplacementCost,
    unifiedPartStatus,
    onBlurReplacementCostNumber,
    costValidityHeight,
  } = useCostFormFieldsContext();

  const inactiveHelperText =
    unifiedPartStatus === QuotePartUnifiedStatus.INACTIVE
      ? [
          <Typography
            key="unifiedPartInactive"
            level={Level.BODY_XS}
            color={Color.WARNING}
            startDecorator={<WarningCircle />}
          >
            {t('labelUnifiedPartInactive')}
          </Typography>,
        ]
      : [];

  const replacementRadio = (
    <Radio
      value={PriceBasedOn.REPLACEMENT}
      label={t('labelUseReplacementCostToCalculatePrice')}
      disabled={isPriceLocked || disablePricingFields}
      sx={{ mb: isAlternateVersionEnabled ? '28px !important' : 0 }}
    />
  );

  const calculatedStandardCost = (
    <Typography level={Level.TITLE_MD} sx={{ marginTop: 1, width: 'fit-content' }}>
      {formatStandardCost(replacementStandardCostValue)}
    </Typography>
  );

  // Deferred lifecycle: the standard cost is materialized only when replacement pricing is
  // selected. Show the value when present; a spinner ONLY while replacement pricing is actively
  // deriving it (a quoted-mode recalc also flips isCalculating but must not imply a derive that
  // isn't happening — it falls through to the deferred placeholder); a warning when replacement
  // pricing is selected but the value is unavailable (e.g. a recost cleared it); otherwise explain
  // the deliberate empty state instead of rendering a misleading 0.
  let calculatedStandardCostContent: JSX.Element;

  if (replacementStandardCostValue) {
    calculatedStandardCostContent = calculatedStandardCost;
  } else if (isCalculating && priceBasedOn === PriceBasedOn.REPLACEMENT) {
    calculatedStandardCostContent = <CircularProgress size="sm" sx={{ marginTop: 1 }} />;
  } else if (priceBasedOn === PriceBasedOn.REPLACEMENT) {
    calculatedStandardCostContent = (
      <Tooltip title={t('warningReplacementStandardCostUnavailable')} color={Color.WARNING}>
        {calculatedStandardCost}
      </Tooltip>
    );
  } else {
    calculatedStandardCostContent = (
      <Tooltip title={t('messageReplacementStandardCostDeferred')} color={Color.PRIMARY}>
        {calculatedStandardCost}
      </Tooltip>
    );
  }

  return (
    <Stack sx={{ gap: 2, height: '100%', justifyContent: 'space-between' }}>
      <Stack sx={{ gap: 'inherit' }}>
        <Typography level={Level.TITLE_MD}>{t('labelReplacementCost')}</Typography>

        <FormField
          name="isReplacementCostCustom"
          type={InputType.SWITCH}
          inputProps={{
            label: t('labelOverwriteReplacementCost'),
            control,
            error: errors?.isReplacementCostCustom,
            onChange: onReplacementCostCustomChange,
            disabled: disabledIsReplacementCostCustom || disablePricingFields,
            variant:
              disabledIsReplacementCostCustom || disablePricingFields
                ? Variant.OUTLINED
                : Variant.SOLID,
            disabledTooltip: !allowOverrideReplacementCost
              ? t('messageAllowOverrideReplacementCost')
              : null,
          }}
        />

        <FormField
          name="replacementCost"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.replacementCost,
            label: t('labelReplacementCost'),
            placeholder: t('labelReplacementCost'),
            onBlur: onBlurReplacementCost,
            disabled: !isReplacementCostCustomValue || disablePricingFields,
            ...getCostInputProps(costDecimals),
            required: isReplacementCostCustomValue && requiredReplacementCost,
            helperTexts: inactiveHelperText,
          }}
        />

        <FormField
          name="replacementCostNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelReplacementCostNumber'),
            placeholder: t('labelReplacementCostNumber'),
            control,
            error: errors?.replacementCostNumber,
            onBlur: onBlurReplacementCostNumber,
            // Deliberately NOT disabled while the prices call runs (no isCalculating/
            // disablePricingFields): the number is user-entered and typed mid-call; the response
            // writes it only while the Overwrite toggle is off (see processCostResponse).
            disabled: !isReplacementCostCustomValue || isExternalDisabled,
            required: requiredReplacementCostNumber,
          }}
        />

        {/* Level with the Quote Cost card's rows under its Validity Dates block. */}
        {!!costValidityHeight && <Box aria-hidden sx={{ height: costValidityHeight }} />}

        <Box>
          <FormLabel>{t('labelCalculatedStandardCost')}</FormLabel>
          {calculatedStandardCostContent}
        </Box>

        <Box>
          <FormLabel>{t('labelCostSource')}</FormLabel>
          {isNoReplacementCostSource ? (
            <TextLine text={t('messageNoCost')} bold />
          ) : (
            <Typography level={Level.BODY_SM} sx={{ fontWeight: 600 }}>
              {replacementCostSourceLabel.long}
            </Typography>
          )}
        </Box>
      </Stack>

      {isPriceLocked ? (
        <Tooltip title={t('messageUseReplacementCostDisabledPriceLocked')} color={Color.DANGER}>
          {replacementRadio}
        </Tooltip>
      ) : (
        replacementRadio
      )}
    </Stack>
  );
};
